import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@rudi/ui/select';
import { Switch } from '@rudi/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@rudi/ui/tabs';
import { ExternalLink, GitBranch, RefreshCw, Video, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, QueryState } from '../components/page';
import {
  useDispatchFeedbackFix,
  useFeedback,
  useFeedbackScreenshot,
  useTenants,
  useUpdateFeedbackStatus,
} from '../api/hooks';
import type {
  AdminTenant,
  Feedback,
  FeedbackAutomationEvent,
  FeedbackListParams,
  FeedbackScreenshot,
  FeedbackSource,
  FeedbackStatus,
  FeedbackUnderstanding,
} from '../api/types';
import {
  STATUS_LABELS,
  automationBadgeVariant,
  automationLabel,
  branchUrl,
  canStartFix,
  confidenceBadgeVariant,
  confidenceLevel,
  dispatchErrorText,
  eventReasonText,
  feedbackLoadError,
  formatBytes,
  replaySearch,
  safeHttpUrl,
  signalText,
  signalWeight,
} from './feedback';
import { FeedbackAutomationPanel } from './FeedbackAutomation';
import { ConfirmDialog } from './practice-agents';
import { FeedbackScreenshots } from './FeedbackScreenshots';

const ALL = 'all';
const PAGE_SIZE = 100;

/** Statuses an operator moves a ticket to. Drafts are the Support agent's own. */
const STATUSES: { label: string; value: FeedbackStatus }[] = [
  { label: 'New', value: 'new' },
  { label: 'Triaging', value: 'triaging' },
  { label: 'Closed', value: 'closed' },
];

function statusBadgeVariant(status: FeedbackStatus): 'default' | 'secondary' | 'outline' {
  if (status === 'new') return 'default';
  if (status === 'triaging') return 'secondary';
  return 'outline';
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function shortId(value: string): string {
  return value.length <= 10 ? value : `${value.slice(0, 8)}...`;
}

function tenantLabel(tenants: AdminTenant[], tenantId: string): string {
  return tenants.find(t => t.id === tenantId)?.name ?? shortId(tenantId);
}

function filer(entry: Feedback): string {
  if (entry.source === 'agent') return entry.filedByAgent ?? 'support agent';
  return entry.userDisplayName ?? 'user';
}

function replayLinkLabel(entry: Feedback): string {
  if (!entry.replayWindowStart || !entry.replayWindowEnd) return 'Watch replay';
  const minutes = Math.round(
    (Date.parse(entry.replayWindowEnd) - Date.parse(entry.replayWindowStart)) / 60_000
  );
  return minutes > 0 ? `Watch replay (last ${minutes} min)` : 'Watch replay';
}

function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-4 space-y-1">
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function ConfidenceBadge({ score }: { score: number }) {
  const level = confidenceLevel(score);
  return (
    <Badge variant={confidenceBadgeVariant(level)} title="Ticket confidence, 0 to 100">
      {level} confidence · {score}
    </Badge>
  );
}

function Understanding({ understanding }: { understanding: FeedbackUnderstanding }) {
  const steps = understanding.steps ?? [];
  const openQuestions = understanding.openQuestions ?? [];
  const hasAny =
    understanding.intent ||
    understanding.observed ||
    understanding.expected ||
    steps.length > 0 ||
    openQuestions.length > 0 ||
    understanding.userConfirmed !== undefined;
  if (!hasAny) return null;
  return (
    <DetailSection title="What the Support agent understood">
      <dl className="space-y-3 rounded border bg-background p-3">
        {understanding.intent && (
          <div>
            <dt className="text-xs text-muted-foreground">The user wanted to</dt>
            <dd>{understanding.intent}</dd>
          </div>
        )}
        {(understanding.observed || understanding.expected) && (
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <dt className="text-xs text-muted-foreground">What happened</dt>
              <dd>{understanding.observed || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">What should have happened</dt>
              <dd>{understanding.expected || '-'}</dd>
            </div>
          </div>
        )}
        {steps.length > 0 && (
          <div>
            <dt className="text-xs text-muted-foreground">Steps</dt>
            <dd>
              <ol className="list-decimal space-y-0.5 pl-5">
                {steps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </dd>
          </div>
        )}
        {openQuestions.length > 0 && (
          <div>
            <dt className="text-xs text-muted-foreground">Open questions</dt>
            <dd>
              <ul className="list-disc space-y-0.5 pl-5">
                {openQuestions.map((question, index) => (
                  <li key={index}>{question}</li>
                ))}
              </ul>
            </dd>
          </div>
        )}
        {understanding.userConfirmed !== undefined && (
          <div>
            <dt className="text-xs text-muted-foreground">Confirmed by the user</dt>
            <dd>{understanding.userConfirmed ? 'Yes' : 'No'}</dd>
          </div>
        )}
      </dl>
    </DetailSection>
  );
}

function ConfidenceDetail({ entry }: { entry: Feedback }) {
  if (entry.confidence === null || entry.confidence === undefined) return null;
  const signals = entry.confidenceSignals ?? [];
  return (
    <DetailSection title="Confidence">
      <div className="flex items-center gap-2">
        <ConfidenceBadge score={entry.confidence} />
      </div>
      {signals.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-muted-foreground">
            How the score came about ({signals.length} {signals.length === 1 ? 'signal' : 'signals'}
            )
          </summary>
          <ul className="mt-2 space-y-1" aria-label="Confidence signals">
            {signals.map((signal, index) => (
              <li key={`${signal.signal}-${index}`} className="flex gap-3">
                <span className="w-10 shrink-0 text-right font-mono text-xs leading-5">
                  {signalWeight(signal.weight)}
                </span>
                <span>{signalText(signal)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </DetailSection>
  );
}

function formatEventTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const LINK_CLASS = 'inline-flex items-center gap-1 text-muted-foreground hover:text-foreground';

function AutomationTimeline({
  events,
  fallbackPrUrl,
}: {
  events: FeedbackAutomationEvent[];
  fallbackPrUrl: string | null;
}) {
  return (
    <ol className="mt-3 space-y-2 border-l pl-4" aria-label="Automatic fix history">
      {events.map((event, index) => {
        const prUrl = safeHttpUrl(event.prUrl);
        const branch = event.branch?.trim();
        const branchHref = branch ? branchUrl(branch, event.prUrl ?? fallbackPrUrl) : null;
        const reason = eventReasonText(event);
        const note = event.note?.trim();
        return (
          <li key={`${event.at}-${index}`} className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <time dateTime={event.at} title={event.at} className="text-xs text-muted-foreground">
                {formatEventTime(event.at)}
              </time>
              <Badge variant={automationBadgeVariant(event.state)}>
                {automationLabel(event.state)}
              </Badge>
              {prUrl && (
                <a href={prUrl} target="_blank" rel="noreferrer" className={LINK_CLASS}>
                  <ExternalLink className="h-3.5 w-3.5" />
                  Pull request
                </a>
              )}
              {branch &&
                (branchHref ? (
                  <a href={branchHref} target="_blank" rel="noreferrer" className={LINK_CLASS}>
                    <GitBranch className="h-3.5 w-3.5" />
                    <span className="font-mono text-xs">{branch}</span>
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <GitBranch className="h-3.5 w-3.5" />
                    <span className="font-mono text-xs">{branch}</span>
                  </span>
                ))}
            </div>
            {reason && <div>{reason}</div>}
            {note && (
              <div
                className={
                  // A not_sent note is Rudi's own reason, not the agent's aside.
                  event.state === 'not_sent'
                    ? 'whitespace-pre-wrap'
                    : 'whitespace-pre-wrap text-muted-foreground'
                }
              >
                {note}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function AutomationDetail({ entry }: { entry: Feedback }) {
  const events = entry.automationEvents ?? [];
  if (!entry.automationState && events.length === 0) return null;
  const prUrl = safeHttpUrl(entry.automationPrUrl);
  return (
    <DetailSection title="Automatic fix">
      {entry.automationState && (
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant={automationBadgeVariant(entry.automationState)}>
            {automationLabel(entry.automationState)}
          </Badge>
          {prUrl && (
            <a href={prUrl} target="_blank" rel="noreferrer" className={LINK_CLASS}>
              <ExternalLink className="h-3.5 w-3.5" />
              Open pull request
            </a>
          )}
          {entry.automationUpdatedAt && (
            <span className="text-xs text-muted-foreground">
              Updated {formatRelative(entry.automationUpdatedAt)}
            </span>
          )}
        </div>
      )}
      {events.length > 0 && <AutomationTimeline events={events} fallbackPrUrl={prUrl} />}
    </DetailSection>
  );
}

/** An object URL for the blob, revoked when the blob changes or the component unmounts. */
function useObjectUrl(blob: Blob | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);
  return url;
}

function ScreenshotThumbnail({
  feedbackId,
  screenshot,
}: {
  feedbackId: string;
  screenshot: FeedbackScreenshot;
}) {
  const image = useFeedbackScreenshot(feedbackId, screenshot.id);
  const url = useObjectUrl(image.data);
  const size = screenshot.size == null ? '' : formatBytes(screenshot.size);
  const caption = size ? `${screenshot.filename} · ${size}` : screenshot.filename;
  const frame = 'block h-28 w-40 overflow-hidden rounded-3xl border bg-background';
  return (
    <li className="w-40 space-y-1">
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" title={caption} className={frame}>
          <img src={url} alt={screenshot.filename} className="h-full w-full object-cover" />
        </a>
      ) : (
        <div
          title={caption}
          className={`${frame} flex items-center justify-center p-3 text-center text-xs text-muted-foreground`}
        >
          {image.isError ? "Couldn't load screenshot" : 'Loading…'}
        </div>
      )}
      <div className="truncate text-xs text-muted-foreground" title={caption}>
        {caption}
      </div>
    </li>
  );
}

/** The images the reporter shared; a Rudi without screenshots sends none. */
function Screenshots({ entry }: { entry: Feedback }) {
  const screenshots = entry.screenshots ?? [];
  if (screenshots.length === 0) return null;
  return (
    <DetailSection title="Screenshots">
      <ul className="flex flex-wrap gap-3" aria-label="Screenshots">
        {screenshots.map(screenshot => (
          <ScreenshotThumbnail key={screenshot.id} feedbackId={entry.id} screenshot={screenshot} />
        ))}
      </ul>
    </DetailSection>
  );
}

export type FeedbackTab = 'tickets' | 'automation';

export interface FeedbackSearch {
  tab?: FeedbackTab;
}

export function validateFeedbackSearch(search: Record<string, unknown>): FeedbackSearch {
  return { tab: search.tab === 'automation' ? 'automation' : undefined };
}

export function FeedbackRoutePage() {
  const search = useSearch({ from: '/feedback' });
  const navigate = useNavigate({ from: '/feedback' });
  return (
    <FeedbackPage
      tab={search.tab ?? 'tickets'}
      onTabChange={tab =>
        void navigate({
          search: previous => ({ ...previous, tab: tab === 'tickets' ? undefined : tab }),
        })
      }
    />
  );
}

/** Tickets, and the Automation tab that sets where confident ones go. */
export function FeedbackPage({
  tab: routeTab,
  onTabChange,
}: {
  tab?: FeedbackTab;
  onTabChange?: (tab: FeedbackTab) => void;
} = {}) {
  const [localTab, setLocalTab] = useState<FeedbackTab>(routeTab ?? 'tickets');
  const tab = onTabChange ? (routeTab ?? 'tickets') : localTab;
  const select = (value: string) => {
    const next: FeedbackTab = value === 'automation' ? 'automation' : 'tickets';
    if (onTabChange) onTabChange(next);
    else setLocalTab(next);
  };

  return (
    <div>
      <PageHeader
        title="Feedback"
        description="Bugs, improvements, and feature requests filed by the Support agent and users, across all tenants."
      />
      <Tabs value={tab} onValueChange={select}>
        <TabsList aria-label="Feedback views" className="mb-4">
          <TabsTrigger value="tickets">Tickets</TabsTrigger>
          <TabsTrigger value="automation">Automation</TabsTrigger>
        </TabsList>
        <TabsContent value="tickets">
          <FeedbackTickets />
        </TabsContent>
        <TabsContent value="automation">
          <FeedbackAutomationPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FeedbackTickets() {
  const [status, setStatus] = useState(ALL);
  const [source, setSource] = useState(ALL);
  const [category, setCategory] = useState('');
  const [showDrafts, setShowDrafts] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const params = useMemo<FeedbackListParams>(
    () => ({
      status: status === ALL ? undefined : (status as FeedbackStatus),
      source: source === ALL ? undefined : (source as FeedbackSource),
      category: category.trim() || undefined,
      include_drafts: showDrafts || status === 'draft' ? true : undefined,
      include_closed: showClosed || status === 'closed' ? undefined : false,
      limit: PAGE_SIZE,
      offset: 0,
    }),
    [category, showClosed, showDrafts, source, status]
  );

  const feedback = useFeedback(params);
  const tenants = useTenants();
  const updateStatus = useUpdateFeedbackStatus();
  const dispatchFix = useDispatchFeedbackFix();
  const [overrideTicket, setOverrideTicket] = useState<Feedback | null>(null);

  const entries = useMemo(() => feedback.data?.items ?? [], [feedback.data?.items]);
  const tenantList = useMemo(() => tenants.data?.tenants ?? [], [tenants.data?.tenants]);

  const startFix = (entry: Feedback, overridePatientData = false) =>
    dispatchFix.mutate(
      { feedbackId: entry.id, overridePatientData },
      {
        onSuccess: () => {
          setOverrideTicket(null);
          toast.success('Fix started');
        },
        onError: error => toast.error(dispatchErrorText(error)),
      }
    );

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36" aria-label="Status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All status</SelectItem>
            <SelectItem value="draft">{STATUS_LABELS.draft}</SelectItem>
            {STATUSES.map(s => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any source</SelectItem>
            <SelectItem value="agent">Support agent</SelectItem>
            <SelectItem value="user">User</SelectItem>
          </SelectContent>
        </Select>

        <Input
          value={category}
          onChange={e => setCategory(e.target.value)}
          placeholder="Category, e.g. bug"
          className="w-44"
        />

        <label className="flex items-center gap-2 text-sm">
          <Switch aria-label="Show drafts" checked={showDrafts} onCheckedChange={setShowDrafts} />
          Show drafts
        </label>

        <label className="flex items-center gap-2 text-sm">
          <Switch aria-label="Show closed" checked={showClosed} onCheckedChange={setShowClosed} />
          Show closed
        </label>

        <div className="ml-auto flex items-center gap-3">
          {feedback.data && (
            <span className="text-xs text-muted-foreground">
              {feedback.data.total} {feedback.data.total === 1 ? 'entry' : 'entries'}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={feedback.isFetching}
            onClick={() => void feedback.refetch()}
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      <QueryState
        isLoading={feedback.isLoading}
        error={feedbackLoadError(feedback.error, status)}
        empty={entries.length === 0}
      >
        <div className="space-y-2">
          {entries.map(entry => {
            const isOpen = expandedId === entry.id;
            const replay = replaySearch(entry);
            const starting =
              dispatchFix.isPending && dispatchFix.variables?.feedbackId === entry.id;
            return (
              <div key={entry.id} className="rounded-lg border bg-card text-sm">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setExpandedId(isOpen ? null : entry.id)}
                  onKeyDown={e => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedId(isOpen ? null : entry.id);
                    }
                  }}
                  className="flex w-full cursor-pointer items-start justify-between gap-4 p-4 text-left hover:bg-muted/30"
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {entry.status === 'draft' ? (
                        <Badge variant="outline" className="border-dashed">
                          {entry.status}
                        </Badge>
                      ) : (
                        <Badge variant={statusBadgeVariant(entry.status)}>{entry.status}</Badge>
                      )}
                      <Badge variant="outline">{entry.category}</Badge>
                      {entry.priority && <Badge variant="outline">{entry.priority}</Badge>}
                      {typeof entry.confidence === 'number' && (
                        <ConfidenceBadge score={entry.confidence} />
                      )}
                      {entry.automationState && (
                        <Badge variant={automationBadgeVariant(entry.automationState)}>
                          {automationLabel(entry.automationState)}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {tenantLabel(tenantList, entry.tenantId)}
                      </span>
                    </div>
                    <div className="font-medium">{entry.summary}</div>
                    <div className="text-xs text-muted-foreground">
                      Filed by {filer(entry)} · {formatRelative(entry.createdAt)}
                      {entry.triagedAt && entry.triagedByDisplayName && (
                        <>
                          {' '}
                          · Triaged by {entry.triagedByDisplayName}{' '}
                          {formatRelative(entry.triagedAt)}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                    {canStartFix(entry) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        disabled={dispatchFix.isPending}
                        onClick={e => {
                          e.stopPropagation();
                          if (entry.automationState === 'needs_human') setOverrideTicket(entry);
                          else startFix(entry);
                        }}
                      >
                        <Wrench className="h-3.5 w-3.5" />
                        {starting
                          ? 'Starting…'
                          : entry.automationState === 'needs_human'
                            ? 'Start fix anyway'
                            : 'Start fix'}
                      </Button>
                    )}
                    {STATUSES.filter(s => s.value !== entry.status).map(s => (
                      <Button
                        key={s.value}
                        variant="outline"
                        size="sm"
                        disabled={updateStatus.isPending}
                        onClick={e => {
                          e.stopPropagation();
                          updateStatus.mutate({ feedbackId: entry.id, status: s.value });
                        }}
                      >
                        → {s.label}
                      </Button>
                    ))}
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t bg-muted/20 p-4">
                    <pre className="whitespace-pre-wrap font-sans">{entry.body}</pre>
                    {entry.understanding && <Understanding understanding={entry.understanding} />}
                    <ConfidenceDetail entry={entry} />
                    <AutomationDetail entry={entry} />
                    {entry.screenshots?.length ? (
                      <Screenshots entry={entry} />
                    ) : entry.chatThreadId ? (
                      <FeedbackScreenshots feedbackId={entry.id} />
                    ) : null}
                    {replay && (
                      <div className="mt-4">
                        <Link
                          to="/replays"
                          search={replay}
                          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                        >
                          <Video className="h-3.5 w-3.5" />
                          {replayLinkLabel(entry)}
                        </Link>
                      </div>
                    )}
                    {entry.context && Object.keys(entry.context).length > 0 && (
                      <DetailSection title="Context">
                        <pre className="overflow-auto rounded border bg-background p-3 text-xs">
                          {JSON.stringify(entry.context, null, 2)}
                        </pre>
                      </DetailSection>
                    )}
                    {entry.triageNote && (
                      <DetailSection title="Triage note">
                        <div>{entry.triageNote}</div>
                      </DetailSection>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </QueryState>
      <ConfirmDialog
        open={overrideTicket !== null}
        title="Start fix despite the warning?"
        description="This ticket was marked as needing a human. Review its text before continuing. Starting it anyway overrides the patient-data warning and sends the ticket to Code. Your approval is recorded in the fix history."
        confirmLabel={dispatchFix.isPending ? 'Starting…' : 'Start fix anyway'}
        pending={dispatchFix.isPending}
        onConfirm={() => {
          if (overrideTicket) startFix(overrideTicket, true);
        }}
        onClose={() => setOverrideTicket(null)}
      />
    </div>
  );
}
